import React from 'react';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { CheckCircle2 } from "lucide-react";

// 1. 核心渲染函式
const renderInline = (str, keyPrefix) => {
  const parts = String(str || "").split(/(\$[^\$]+\$)/g);
  return parts.map((part, i) => {
    if (part.startsWith("$") && part.endsWith("$")) {
      return <span key={`${keyPrefix}-m-${i}`} className="inline-block align-middle my-1"><InlineMath math={part.slice(1, -1).trim()} /></span>;
    }
    const segs = part.split(/(\*\*.*?\*\*)/g);
    return (
      <span key={`${keyPrefix}-t-${i}`} className="whitespace-pre-wrap">
        {segs.map((seg, j) =>
          seg.startsWith("**") && seg.endsWith("**") ? (
            <strong key={`${keyPrefix}-b-${i}-${j}`} className="text-orange-900 font-bold bg-orange-100/50 px-1 rounded box-decoration-clone">{seg.slice(2, -2)}</strong>
          ) : (<span key={`${keyPrefix}-s-${i}-${j}`}>{seg}</span>)
        )}
      </span>
    );
  });
};

const renderLine = (line, key) => (
  <p key={key} className="whitespace-pre-wrap leading-relaxed py-0.5 break-words text-slate-700 font-medium tracking-wide text-justify my-1">
      {renderInline(line, `ln-${key}`)}
  </p>
);

const renderSimplePretty = (raw) => {
  const s = String(raw || "").replace(/\r\n/g, "\n").trim();
  if (!s) return null;

  const ansMatch = s.match(/(✅\s*)?(答案|Ans|答)\s*[:：]\s*(.+)$/im) || s.match(/(所以|因此)[^。\n]*([=＝]\s*[-\d\.]+|為\s*[-\d\.]+)[^。\n]*/);
  let answer = "";
  if (ansMatch) answer = (ansMatch[3] || ansMatch[0] || "").trim();

  const lines = s.split("\n").map((x) => x.trim()).filter(Boolean).filter(line => {
      if (answer && line.includes(answer)) return false;
      if (/^(答案|Ans|答)\s*[:：]/i.test(line)) return false;
      return true;
  });
  
  const intro = lines[0] && lines[0].length >= 18 && !/[:：=＝]/.test(lines[0]) ? lines[0] : "";
  const rest = intro ? lines.slice(1) : lines;
  const bullets = rest.slice(0, 6).map((t) => t.replace(/^(因此|所以|結論|故)\s*/g, "").trim());

  return (
    <div className="space-y-4">
      {intro && (
        <div className="p-5 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-amber-50 shadow-sm">
          <div className="font-black text-orange-700 mb-1 flex items-center gap-2">👨‍🏫 老師先講一句</div>
          <div className="text-slate-700 leading-relaxed">{renderInline(intro, "simp-intro")}</div>
        </div>
      )}
      {bullets.length > 0 && (
        <div className="p-5 rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-emerald-50 shadow-sm">
          <div className="font-black text-slate-700 mb-2 flex items-center gap-2">🔎 你要抓的重點</div>
          <ul className="space-y-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex gap-3">
                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                <div className="text-slate-700 leading-relaxed">{renderInline(b, `simp-b-${i}`)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {answer && (
        <div className="p-5 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-lime-50 shadow-sm">
          <div className="font-black text-green-700 mb-1 flex items-center gap-2">✅ 最終答案</div>
          <div className="text-slate-900 font-black text-lg leading-relaxed">{renderInline(answer, "simp-ans")}</div>
        </div>
      )}
    </div>
  );
};

const renderStepCard = (step, idx) => {
  const stepNo = step.title.match(/步驟\s*(\d+)/)?.[1] || String(idx + 1);
  const cleanTitle = step.title.replace(/^【\s*步驟\s*\d+】/, "").trim();
  const shouldShow = (txt) => {
      if (!txt) return false;
      const t = txt.replace(/[\(\)（）]/g, "").trim();
      return !["略", "無", "省略", "同上"].includes(t);
  };

  return (
    <div key={`st-${idx}`} className="rounded-[1.25rem] border border-orange-100 bg-white shadow-sm p-5 space-y-4 my-2">
      <div className="flex items-start gap-3 border-b border-orange-50 pb-2">
        <div className="shrink-0 w-8 h-8 rounded-xl bg-orange-100 text-orange-700 font-black flex items-center justify-center shadow-sm">{stepNo}</div>
        <div className="font-black text-orange-700 text-lg leading-relaxed pt-0.5">{renderInline(cleanTitle || "計算過程", `st-title-${idx}`)}</div>
      </div>
      {shouldShow(step.known) && (
          <div className="flex gap-3"><span className="shrink-0 text-slate-400 font-bold text-sm mt-1">已知</span><div className="flex-1">{renderInline(step.known, `st-k-${idx}`)}</div></div>
      )}
      {shouldShow(step.formula) && (
          <div className="flex gap-3 items-start"><span className="shrink-0 text-slate-400 font-bold text-sm mt-2">公式</span><div className="flex-1 bg-orange-50/50 border-l-4 border-orange-300 rounded-r-lg px-4 py-3 shadow-sm">{renderInline(step.formula, `st-f-${idx}`)}</div></div>
      )}
      {shouldShow(step.concl) && (
          <div className="flex gap-3 items-start"><span className="shrink-0 text-slate-400 font-bold text-sm mt-2">結論</span><div className="flex-1 flex gap-2 items-start bg-green-50/50 p-3 rounded-lg border border-green-100 shadow-sm"><CheckCircle2 size={18} className="text-green-600 mt-0.5 shrink-0"/><div className="text-slate-800 font-bold leading-relaxed">{renderInline(step.concl, `st-c-${idx}`)}</div></div></div>
      )}
      {step.notes && (
           <div className="flex gap-3 pt-1"><span className="shrink-0 text-slate-400 font-bold text-sm mt-1">說明</span><div className="flex-1 whitespace-pre-wrap text-slate-600 leading-relaxed">{renderInline(step.notes, `st-n-${idx}`)}</div></div>
      )}
    </div>
  );
};

const patchTextOutsideMath = (raw) => {
  let s = String(raw || "").replace(/^Simple Answer:|^Simple:|^Answer:/i, "").replace(/\^o|\^\{o\}/g, "^{\\circ}").replace(/\\/g, "\\");
  s = s.replace(/([^\n])\s*(【[^】]+】)/g, "$1\n\n$2"); 
  const chunks = s.split(/(\$[^\$]+\$)/g);
  const patched = chunks.map((chunk) => {
    if (chunk.startsWith("$") && chunk.endsWith("$")) return chunk;
    let t = chunk;
    const looksLikeLatex = /\\(frac|sqrt|angle|triangle|overline|vec|cdot|circ)/.test(t) && !/\$[^$]+\$/.test(t);
    if (looksLikeLatex && /\\[a-zA-Z]+/.test(t)) { t = ` $${t.trim()}$ `; }
    t = t.replace(/\\wideparen\s*\{?\s*([A-Za-z0-9]{1,12})\s*\}?/g, (_, p1) => ` $\\overset{\\frown}{${p1}}$ `);
    t = t.replace(/\\angle\s*\{?\s*([A-Za-z0-9]{1,12})\s*\}?/g, (_, p1) => ` $\\angle ${p1}$ `);
    t = t.replace(/∠\s*([A-Za-z0-9]{1,12})/g, (_, p1) => ` $\\angle ${p1}$ `);
    t = t.replace(/\\triangle\s*\{?\s*([A-Za-z0-9]{1,12})\s*\}?/g, (_, p1) => ` $\\triangle ${p1}$ `);
    t = t.replace(/△\s*([A-Za-z0-9]{1,12})/g, (_, p1) => ` $\\triangle ${p1}$ `);
    t = t.replace(/\\overleftrightarrow\s*\{([^}]*)\}/g, (_, p1) => ` $\\overleftrightarrow{${p1}}$ `);
    t = t.replace(/\\overline\s*\{([^}]*)\}/g, (_, p1) => ` $\\overline{${p1}}$ `);
    t = t.replace(/\\overrightarrow\s*\{([^}]*)\}/g, (_, p1) => ` $\\overrightarrow{${p1}}$ `);
    t = t.replace(/\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, (_, a, b) => ` $\\frac{${a}}{${b}}$ `);
    t = t.replace(/\\sqrt\s*\{([^{}]*)\}/g, (_, a) => ` $\\sqrt{${a}}$ `);
    t = t.replace(/(\d+)\s*°/g, (_, d) => ` $${d}^\\circ$ `);
    t = t.replace(/\$\s+\$/g, " ");
    t = t.replace(/\$\$/g, "$");
    return t;
  });
  return patched.join("");
};

const ResultFormatter = ({ text, variant = "detailed" }) => {
  if (!text) return null;
  let preProcessed = patchTextOutsideMath(text);
  preProcessed = preProcessed.replace(/】\s*【步驟/g, "】\n\n【步驟").replace(/([^\n])\s*(【步驟\s*\d+)/g, "$1\n\n$2");

  if (variant === "simple") {
    return <div className="px-1">{renderSimplePretty(preProcessed)}</div>;
  }

  const parseBlocks = (rawText) => {
    const ls = String(rawText || "").replace(/\r\n/g, "\n").split("\n");
    const blocks = [];
    const isStep = (s) => /^【\s*步驟\s*(\d+)/.test((s || "").trim());
    const getStepNum = (s) => s.match(/^【\s*步驟\s*(\d+)/)?.[1];
    const isSectionTitle = (s) => /^（\s*\d+\s*\.\s*[^）]+）/.test((s || "").trim());
    let i = 0;
    while (i < ls.length) {
      const rawLine = ls[i] ?? "";
      const line = rawLine.trim();
      if (!line) {
        if (blocks.length > 0 && blocks[blocks.length-1].type !== 'blank') blocks.push({ type: "blank" });
        i += 1; continue;
      }
      if (isSectionTitle(line)) { blocks.push({ type: "section", text: rawLine.trim() }); i += 1; continue; }
      if (!isStep(line)) { blocks.push({ type: "line", text: rawLine }); i += 1; continue; }

      const title = line;
      i += 1;
      const buf = [];
      while (i < ls.length && !isStep(ls[i])) { buf.push(ls[i] ?? ""); i += 1; }

      const buckets = { known: [], formula: [], concl: [], notes: [] };
      const labelToKey = (lab) => {
        if (lab === "已知") return "known";
        if (lab === "公式") return "formula";
        if (["結論", "因此", "所以", "推論", "解"].includes(lab)) return "concl";
        return "notes";
      };
      const splitByLabels = (line) => {
        const t = String(line || "");
        const re = /(已知|公式|結論|因此|所以|推論|解)\s*[:：]/g;
        let m, lastLabel = null, lastIdx = 0;
        const out = [];
        while ((m = re.exec(t)) !== null) {
          const label = m[1];
          const idx = m.index;
          if (lastLabel !== null) out.push({ label: lastLabel, content: t.slice(lastIdx, idx).trim() });
          else { const lead = t.slice(0, idx).trim(); if (lead) out.push({ label: null, content: lead }); }
          lastLabel = label; lastIdx = re.lastIndex;
        }
        if (lastLabel !== null) out.push({ label: lastLabel, content: t.slice(lastIdx).trim() });
        else out.push({ label: null, content: t.trim() });
        return out;
      };
      let currentKey = null;
      for (const raw of buf) {
        const line = String(raw || "");
        if (!line.trim()) continue;
        const parts = splitByLabels(line);
        for (const p of parts) {
          if (p.label) { currentKey = labelToKey(p.label); if (p.content) buckets[currentKey].push(p.content); }
          else { const key = currentKey || "notes"; if (p.content) buckets[key].push(p.content); }
        }
      }
      blocks.push({ type: "step", title, known: buckets.known.join("\n").trim(), formula: buckets.formula.join("\n").trim(), concl: buckets.concl.join("\n").trim(), notes: buckets.notes.join("\n").trim() });
    }
    return blocks;
  };

  const blocks = parseBlocks(preProcessed);
  const hasSteps = blocks.some((b) => b.type === "step");

  if (hasSteps) {
      const lines = String(preProcessed).split(/\n+/).map(s => s.trim()).filter(Boolean);
      const teacherIntro = (!lines[0].startsWith("【步驟") && !lines[0].startsWith("###")) ? lines[0] : null;
      return (
        <div className="space-y-6 tracking-wide text-base md:text-lg text-slate-700 font-medium px-1">
          {teacherIntro && (
            <div className="p-5 rounded-[1.5rem] border border-orange-100 bg-orange-50/30 shadow-sm">
              <div className="font-black text-orange-700 mb-2 flex items-center gap-2">👨‍🏫 老師思路導引</div>
              {renderLine(teacherIntro, "teacher-intro")}
            </div>
          )}
          {blocks.map((b, idx) => {
            if (b.type === "blank") return <div key={idx} className="h-2" />;
            if (b.type === "section") return <div key={idx} className="mt-6 mb-3 font-black text-orange-800 text-lg border-l-4 border-orange-400 pl-3">{b.text}</div>;
            if (b.type === "step") return renderStepCard(b, idx);
            return renderLine(b.text, idx);
          })}
        </div>
      );
  }

  const textLines = String(preProcessed).split("\n");
  const isBullet = (s) => /^(\-|\•|\*)\s+/.test(s.trim());
  const isNumbered = (s) => /^\d+[\.\、]\s+/.test(s.trim());
  const isBracketHeader = (s) => /^【.*】$/.test(s.trim());

  return (
    <div className="space-y-3 text-base md:text-lg text-slate-700 font-medium text-justify tracking-wide px-1">
      {textLines.map((line, idx) => {
        if (!line.trim()) return <div key={idx} className="h-4" />;
        if (isBracketHeader(line)) return <h4 key={idx} className="text-orange-700 font-black text-xl mt-8 mb-3 flex items-center gap-2 border-b-2 border-orange-100 pb-1">{renderInline(line, `bh-${idx}`)}</h4>;
        if (isBullet(line)) {
          const item = line.replace(/^(\-|\•|\*)\s+/, "");
          return <div key={idx} className="flex gap-3 pl-2"><div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" /><div className="flex-1 leading-loose">{renderLine(item, `li-${idx}`)}</div></div>;
        }
        if (isNumbered(line)) {
          const m = line.trim().match(/^(\d+)[\.\、]\s+(.*)$/);
          return (
            <div key={idx} className="flex gap-3 p-4 mb-3 rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)] hover:shadow-md hover:border-orange-200 transition-all duration-300 group">
                <div className="shrink-0 w-8 h-8 rounded-xl bg-orange-100 text-orange-600 font-black text-sm flex items-center justify-center mt-0.5 group-hover:bg-orange-500 group-hover:text-white transition-colors">{m?.[1]||""}</div>
                <div className="flex-1 leading-relaxed text-slate-700 font-bold pt-1">{renderLine(m?.[2]||line, `num-${idx}`)}</div>
            </div>
          );
        }
        return renderLine(line, `l-${idx}`);
      })}
    </div>
  );
};

export default ResultFormatter;
